import { useState, useEffect } from 'react';
import { WHEEL_CONFIG, ANIMATION_CONFIG, UI_CONFIG } from '../constants/index';
import { 
  generateCrystalColors, 
  calculateSegmentProperties, 
  generatePieSlicePath, 
  calculateTextPosition,
  generateSpinParameters 
} from '../utils/wheel.js';

export default function Wheel({ prizes, onSpin, onSpinClick, isSpinning, hasSpun, predeterminedPrize }) {
  const [rotation, setRotation] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);
  const [winningSegment, setWinningSegment] = useState(null);
  const [showHighlight, setShowHighlight] = useState(false);
  const [spinCompleted, setSpinCompleted] = useState(false);

  useEffect(() => {
    if (isSpinning && !isAnimating && !hasSpun && !spinCompleted) {
      startSpin();
    }
  }, [isSpinning]);

  const startSpin = () => {
    if (isAnimating || hasSpun || spinCompleted) return;
    
    setIsAnimating(true);
    setSpinCompleted(true);
    setWinningSegment(null);
    setShowHighlight(false);
    
    // Find the index of the predetermined prize
    let winningIndex = 0;
    if (predeterminedPrize) {
      winningIndex = safePrizes.findIndex(prize => prize.id === predeterminedPrize.id);
      if (winningIndex === -1) {
        winningIndex = 0; // Fallback
      }
    }
    
    // Simple rotation calculation - we don't need precise arrow alignment anymore
    // since we have multiple visual indicators for the winner
    const baseSpins = 3 + Math.random() * 2; // 3-5 spins
    const randomEndRotation = Math.random() * 360; // Random final position
    const finalRotation = baseSpins * 360 + randomEndRotation;
    
    // console.log('🎯 Winning index:', winningIndex);
    // console.log('🎁 Winning prize name:', safePrizes[winningIndex]?.name);
    // console.log('🔄 Final rotation:', finalRotation);
   
    
    const duration = 3000; // 3 seconds
    const startTime = Date.now();
    const startRotation = rotation;
    
    const animate = () => {
      const elapsed = Date.now() - startTime;
      const progress = Math.min(elapsed / duration, 1);
      
      // Easing function for smooth deceleration
      const easeOut = 1 - Math.pow(1 - progress, 3);
      const currentRotation = startRotation + (finalRotation * easeOut);
      
      setRotation(currentRotation);
      
      if (progress < 1) {
        requestAnimationFrame(animate);
      } else {
        setIsAnimating(false);
        
        // Set winning segment and show highlight
        setWinningSegment(winningIndex);
        setShowHighlight(true);
        

        
        // After 1.5 seconds, hide highlight and trigger onSpinComplete
        setTimeout(() => {
          setShowHighlight(false);
          if (onSpin && !hasSpun) {
            // Pass the predetermined prize
            const winningPrize = predeterminedPrize || safePrizes[winningIndex];
            // console.log('🏆 Sending winning prize to parent:', winningPrize);
            onSpin(winningPrize);
          }
        }, 1500);
      }
    };
    
    requestAnimationFrame(animate);
  };

  const handleSpinClick = () => {
    if (!isSpinning && !isAnimating && !hasSpun) {
      // Call the parent's onSpinClick to start the spin
      if (onSpinClick) {
        onSpinClick();
      }
    }
  };

  // Ensure prizes is always an array
  const safePrizes = Array.isArray(prizes) ? prizes : [];
  
  if (safePrizes.length === 0) {
    return (
      <div className="text-center text-gray-500">
        Aucun prix disponible
      </div>
    );
  }

  // Debug: Log the wheel segments order
  // console.log('🎡 Wheel segments order:', safePrizes.map((prize, index) => `${index}: ${prize.name}`));

  const crystalColors = generateCrystalColors(safePrizes.length);
  const { WHEEL_SIZE, WHEEL_RADIUS } = WHEEL_CONFIG;
  const center = WHEEL_SIZE / 2;

  const renderWheelSegment = (prize, index) => {
    const { segmentAngle, startAngle, endAngle, midAngle } = calculateSegmentProperties(safePrizes.length, index);
    const pathData = generatePieSlicePath(center, WHEEL_RADIUS, startAngle, endAngle, segmentAngle);
    const { x, y } = calculateTextPosition(center, WHEEL_RADIUS, midAngle);
    
    const isWinning = showHighlight && winningSegment === index;
    
    return (
      <g key={prize.id}>
        {/* Enhanced winning segment with multiple visual effects */}
        {isWinning && (
          <>
            {/* Outer glow ring */}
            <path
              d={pathData}
              fill="none"
              stroke="rgba(255, 255, 255, 1)"
              strokeWidth="8"
              filter="url(#winnerGlow)"
              style={{
                animation: "winnerPulse 1.5s ease-in-out infinite alternate"
              }}
            />
            {/* Inner highlight ring */}
            <path
              d={pathData}
              fill="none"
              stroke="rgba(255, 255, 255, 0.8)"
              strokeWidth="4"
              style={{
                animation: "winnerPulse 1.5s ease-in-out infinite alternate-reverse"
              }}
            />
          </>
        )}
        
        <path
          d={pathData}
          fill={crystalColors[index]}
          stroke={isWinning ? "rgba(255, 255, 255, 0.9)" : "rgba(255, 255, 255, 0.8)"}
          strokeWidth={isWinning ? "6" : "2"}
          filter={isWinning ? "url(#neonGlow)" : "url(#shadow)"}
          style={{
            animation: isWinning ? "neonPulse 2s ease-in-out infinite alternate" : undefined,
            '--pie-color': crystalColors[index],
            transform: isWinning ? "scale(1.15)" : "scale(1)",
            transformOrigin: `${center}px ${center}px`,
            transition: "transform 0.3s ease-in-out"
          }}
        />
        
        <text
          x={x}
          y={y}
          textAnchor="middle"
          dominantBaseline="middle"
          fill="white"
          fontSize={isWinning ? "12" : "10"}
          fontWeight="bold"
          style={{
            transform: `rotate(${midAngle * 180 / Math.PI}deg)`,
            transformOrigin: `${x}px ${y}px`,
            filter: isWinning ? "drop-shadow(0 0 12px rgba(255, 255, 255, 1)) drop-shadow(0 0 6px var(--pie-color))" : "drop-shadow(0 2px 4px rgba(0, 0, 0, 0.5))",
            transition: "all 0.3s ease-in-out"
          }}
        >
          {prize.name}
        </text>
      </g>
    );
  };



  const renderSpinInfo = () => (
    <div className="text-center mt-6">
      {hasSpun ? (
        <div className="bg-orange-50 border border-orange-200 rounded-xl p-4 mb-4">
          <p className="text-orange-700 text-sm font-medium">
            🎉 Vous avez déjà tourné la roue ! Vous ne pouvez plus tourner à nouveau.
          </p>
          <p className="text-orange-600 text-xs mt-2">
            Vous avez : <span className="font-semibold">0 chance</span>
          </p>
        </div>
      ) : (
        <p className="text-gray-600 text-sm mb-2 text-[12px]">
          Vous avez : <span className="text-orange-600 font-semibold">1 chance</span>
        </p>
      )}
      
      <button
        onClick={handleSpinClick}
        disabled={isSpinning || isAnimating || hasSpun}
        className={`w-full max-w-xs font-bold py-4 px-8 rounded-2xl shadow-lg transition-all duration-300 disabled:cursor-not-allowed ${
          hasSpun 
            ? 'bg-gray-400 text-gray-600 cursor-not-allowed' 
            : 'bg-gradient-to-r from-orange-500 to-yellow-500 text-white hover:shadow-xl transform hover:scale-105'
        } ${
          isSpinning || isAnimating ? ANIMATION_CONFIG.PULSE_ANIMATION : ''
        }`}
      >
        <div className="flex items-center justify-center text-[12px]">
          {hasSpun ? (
            <>
              Déjà Tourné
            </>
          ) : (
            <>
              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
              Tourner Maintenant
            </>
          )}
        </div>
      </button>
    </div>
  );

  return (
    <div className="relative">
      <style dangerouslySetInnerHTML={{
        __html: `
          @keyframes neonPulse {
            0% {
              filter: brightness(1.1) drop-shadow(0 0 8px var(--pie-color, rgba(255, 255, 255, 0.3)));
            }
            100% {
              filter: brightness(1.4) drop-shadow(0 0 15px var(--pie-color, rgba(255, 255, 255, 0.6)));
            }
          }
          
          @keyframes winnerPulse {
            0% {
              opacity: 0.6;
              transform: scale(1);
            }
            100% {
              opacity: 1;
              transform: scale(1.02);
            }
          }
        `
      }} />
      <div className="relative w-80 h-80 mx-auto">
        {/* Wheel Background */}
        <div className="absolute inset-0 bg-gradient-to-br from-orange-100 via-purple-100 to-orange-50 rounded-full border-4 border-orange-300 shadow-2xl"></div>
        
        {/* SVG Wheel with Pie Slices */}
        <svg 
          width={WHEEL_SIZE} 
          height={WHEEL_SIZE} 
          className="absolute inset-0"
          style={{ transform: `rotate(${rotation}deg)` }}
        >
          <defs>
            <filter id="shadow" x="-20%" y="-20%" width="140%" height="140%">
              <feDropShadow dx="2" dy="2" stdDeviation="3" floodColor="rgba(0,0,0,0.3)"/>
            </filter>
            <filter id="neonGlow" x="-50%" y="-50%" width="200%" height="200%">
              <feGaussianBlur stdDeviation="4" result="coloredBlur"/>
              <feMerge> 
                <feMergeNode in="coloredBlur"/>
                <feMergeNode in="SourceGraphic"/>
              </feMerge>
            </filter>
            <filter id="winnerGlow" x="-100%" y="-100%" width="300%" height="300%">
              <feGaussianBlur stdDeviation="8" result="coloredBlur"/>
              <feGaussianBlur stdDeviation="4" result="coloredBlur2"/>
              <feMerge> 
                <feMergeNode in="coloredBlur"/>
                <feMergeNode in="coloredBlur2"/>
                <feMergeNode in="SourceGraphic"/>
              </feMerge>
            </filter>
          </defs>
          
          {safePrizes.map((prize, index) => renderWheelSegment(prize, index))}
        </svg>
        

        


        {/* Winner Announcement Banner */}
        {showHighlight && winningSegment !== null && (
          <div className="absolute -top-24 left-1/2 transform -translate-x-1/2 z-30">
            <div className="bg-gradient-to-r from-yellow-400 via-orange-500 to-red-500 text-white px-8 py-4 rounded-full shadow-2xl border-3 border-white animate-bounce">
              <div className="text-center">
                <div className="text-lg font-bold">🎉 GAGNÉ ! 🎉</div>
                <div className="text-base font-semibold">
                  {predeterminedPrize?.name || safePrizes[winningSegment]?.name}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
      
      {renderSpinInfo()}
    </div>
  );
}